import { useState } from "react";
import { Instagram, Mail } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";
import { WhatsAppIcon } from "../components/Layout";

export default function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", { type: "contact", ...form });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(t("booking.bookingError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO title="Contact — The Nice Picnic, Côte d'Azur"
        description="Contact The Nice Picnic: WhatsApp +33 6 52 88 21 60, hello@thenicepicnic.com, @the.nicepicnic. Let's create your Riviera picnic experience." />
      <section className="pt-36 pb-24 sm:pb-32 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-14">
          <Reveal className="md:col-span-5">
            <p className="overline-label">{t("contact.overline")}</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-ink mt-4">{t("contact.title")}</h1>
            <div className="w-16 h-px bg-gold mt-6" />
            <p className="mt-8 text-earth font-light leading-relaxed">{t("contact.p")}</p>
            <ul className="mt-10 space-y-5">
              <li>
                <a data-testid="contact-whatsapp" href="https://wa.me/33652882160" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-4 group">
                  <span className="w-11 h-11 rounded-full border border-champagne flex items-center justify-center text-olive group-hover:border-terracotta group-hover:text-terracotta transition-colors"><WhatsAppIcon size={18} /></span>
                  <span className="text-sm text-earth">+33 6 52 88 21 60</span>
                </a>
              </li>
              <li>
                <a data-testid="contact-email" href="mailto:hello@thenicepicnic.com" className="flex items-center gap-4 group">
                  <span className="w-11 h-11 rounded-full border border-champagne flex items-center justify-center text-olive group-hover:border-terracotta group-hover:text-terracotta transition-colors"><Mail size={17} /></span>
                  <span className="text-sm text-earth">hello@thenicepicnic.com</span>
                </a>
              </li>
              <li>
                <a data-testid="contact-instagram" href="https://www.instagram.com/the.nicepicnic" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
                  <span className="w-11 h-11 rounded-full border border-champagne flex items-center justify-center text-olive group-hover:border-terracotta group-hover:text-terracotta transition-colors"><Instagram size={17} /></span>
                  <span className="text-sm text-earth">@the.nicepicnic</span>
                </a>
              </li>
            </ul>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7">
            <form onSubmit={submit} className="border border-champagne rounded-3xl p-7 sm:p-10 bg-cream/60" data-testid="contact-form">
              <div className="space-y-5">
                <input data-testid="contact-name" required className="input-elegant" placeholder={t("contact.formName")}
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input data-testid="contact-email-input" required type="email" className="input-elegant" placeholder={t("contact.formEmail")}
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <textarea data-testid="contact-message" required rows={6} className="input-elegant" placeholder={t("contact.formMessage")}
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                <button data-testid="contact-submit-btn" disabled={sending} className="btn-primary w-full disabled:opacity-60">
                  {t("contact.send")}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
